import {HookMatchCriteria, Ng2StateDeclaration, StatesModule, UIRouter} from '@uirouter/angular';
import {trace} from '@uirouter/core';
import {environment} from '../environments/environment';
import {Injector} from '@angular/core';
import {STATE_NAMES} from './common/state-names';
import {ResentPasswordPageComponent} from './pages/resent-password/resent-password.component';
import {NewPasswordPageComponent} from './pages/new-password/new-password.component';
import {SignUpPageComponent} from './pages/sign-up/sign-up.component';
import {resolveLegacyBillingPlan} from './models/auth/sign-up';
import {StorageService} from './original-services/storage.service';
import {SignInPageComponent} from './pages/sign-in/sign-in.component';
import {RestoringPageComponent} from './pages/restoring/restoring.component';

export const ACCOUNT_AUTHENTICATE: Ng2StateDeclaration[] = [
    {
        name: STATE_NAMES.login,
        url: '/login?redirectUrl',
        component: SignInPageComponent,
        params: {
            redirectUrl: null
        }
    },
    {
        name: STATE_NAMES.authorizeSlack,
        url: '/login/:slackId',
        component: SignInPageComponent
    },
    {
        name: STATE_NAMES.userActivate,
        url: '/login/:param1/:param2',
        params: { param1: '', param2: '' },
        component: SignInPageComponent
    },
    {
        name: STATE_NAMES.restoreEmail,
        url: '/restore-email',
        component: RestoringPageComponent
    },
    {
        name: STATE_NAMES.restorePassword,
        url: '/resent-password/:param1/:param2',
        component: ResentPasswordPageComponent,
        params: { param1: '', param2: '' },
    },
    {
        name: STATE_NAMES.newPassword,
        url: '/new-password/:param1/:param2',
        component: NewPasswordPageComponent,
        params: { param1: '', param2: '' },
    },
    {
        name: STATE_NAMES.activate,
        url: '/activate/:param1/:param2',
        component: NewPasswordPageComponent,
        params: { param1: '', param2: '' },
        data: { mode: 'activate' },
    },
    {
        name: STATE_NAMES.signUp,
        url: '/signup/:subscription',
        component: SignUpPageComponent,
        params: { user: '', subscription: '' }
    },
];

/**
 * Placeholder ("future state") standing in for `app` and every authenticated state beneath it.
 *
 * Notes on the shape, all of which are load-bearing:
 * - `app.**` resolves its parent to the ROOT, so it replaces `app` itself, not a child of it. The
 *   lazily loaded module must therefore declare a state literally named `app` (it does � see
 *   `APP_SHELL_STATES`), and `app` must NOT be declared here as well or registration throws
 *   "State 'app' is already defined".
 * - It must NOT be `abstract`. `StateQueueManager.attachRoute` skips URL rules for abstract states,
 *   so an abstract placeholder is unreachable by URL and deep links would never trigger the load.
 * - `url: '/'` compiles to `/{remainder:any}`, i.e. a prefix match on everything. That is safe
 *   because `UrlMatcher.compare` sorts static segments ahead of wildcard params, so the concrete
 *   public routes above still win. A single catch-all is used rather than one placeholder per
 *   section because the authenticated states have no common URL prefix, and a per-section list has
 *   to be maintained by hand � forgetting an entry silently breaks that deep link.
 * - `requireAuth` marks it for the auth guard below, so a logged-out visitor is redirected BEFORE
 *   the chunk is fetched.
 */
const APP_SHELL_PLACEHOLDER: Ng2StateDeclaration = {
    name: `${STATE_NAMES.app}.**`,
    url: '/',
    data: {
        requireAuth: true
    },
    loadChildren: () => import('./app-shell/app-shell.module').then(m => m.AppShellModule).catch(reloadForCurrentBuild)
};

/** First URL segment of every public state — reloading onto one of these would show Sign in again. */
const PUBLIC_URL_SEGMENTS = ACCOUNT_AUTHENTICATE.map(s => s.url.split('/')[1].split('?')[0]);
const APP_SHELL_RELOAD_KEY = 'appShellReloadAt';
const APP_SHELL_RELOAD_GUARD_MS = 60 * 1000;

/**
 * Recovers a tab that was opened before a deploy.
 *
 * The app-shell chunk is content-hashed and every deploy ships a fresh nginx image holding only the
 * new build, so an old `main` bundle asks for an `app-shell-<oldhash>.js` that now 404s. Without this
 * the login succeeded, the navigation into the app was rejected and Sign in kept spinning forever.
 *
 * A reload fetches the current build. The session is already in storage by then, so the user lands
 * in the app: on the deep link they were opening, or — when the failed navigation started from a
 * public page such as Sign in — on `#/`, which `app.home` redirects into the app. A second failure
 * within a minute means the chunk is genuinely unavailable, so the error is rethrown rather than
 * reloading in a loop.
 */
export function reloadForCurrentBuild(
    error: unknown,
    location: Pick<Location, 'hash' | 'reload'> = window.location,
    history: Pick<History, 'replaceState'> = window.history,
    storage: Pick<Storage, 'getItem' | 'setItem'> = window.sessionStorage
): Promise<never> {
    try {
        const lastReload = Number(storage.getItem(APP_SHELL_RELOAD_KEY));
        if (lastReload && Date.now() - lastReload < APP_SHELL_RELOAD_GUARD_MS) {
            return Promise.reject(error);
        }
        storage.setItem(APP_SHELL_RELOAD_KEY, String(Date.now()));
    } catch (_) {
        // Without storage there is no loop guard, so don't risk reloading forever.
        return Promise.reject(error);
    }

    const segment = location.hash.replace(/^#\/?/, '').split(/[/?]/)[0];
    if (PUBLIC_URL_SEGMENTS.indexOf(segment) !== -1) {
        history.replaceState(null, '', '#/');
    }
    location.reload();
    // Never settles: the page is unloading, so the navigation must not fail visibly in the meantime.
    return new Promise<never>(() => {});
}

export const STATES: Ng2StateDeclaration[] = [
    ...ACCOUNT_AUTHENTICATE,
    APP_SHELL_PLACEHOLDER
];

/**
 * Opt-in UI-Router tracing, for diagnosing a page that stops updating while the URL keeps changing.
 *
 * Enabled per browser, not per build: `production` is true on UAT too, so an environment flag would
 * be useless exactly where such reports come from. To turn it on, run
 * `localStorage.setItem('uiRouterTrace', 'on')` in the console and reload; `removeItem` turns it off.
 *
 * UIVIEW logs each `<ui-view>` registering and deregistering; VIEWCONFIG logs every `configUpdated`.
 * A frozen page shows the outlet deregistering and no later `configUpdated` carrying the new page's
 * config. TRANSITION logs each transition's start, redirect and outcome with its from/to states.
 */
function enableRouterTraceWhenRequested(): void {
    try {
        if (localStorage.getItem('uiRouterTrace') === 'on') {
            trace.enable('TRANSITION', 'UIVIEW', 'VIEWCONFIG');
        }
    } catch (_) {
        // Storage can throw in private mode or when site data is blocked; tracing is optional.
    }
}

export function routerConfigFn(router: UIRouter, injector: Injector, module: StatesModule) {
    const storage = injector.get(StorageService);
    enableRouterTraceWhenRequested();

    // Redirect to home page if don't match any rule
    router.urlService.rules.otherwise({state: STATE_NAMES.home});
    const routingService = router.stateService;

    /**
     * Auth gate for everything behind the lazy boundary.
     *
     * The priority is not cosmetic. UI-Router's built-in lazy-load hook is registered by the
     * TransitionService constructor at priority 0, and `onBefore` hooks run in descending priority
     * with ties broken by registration order � so a default-priority guard registered here (later)
     * would run only AFTER the app chunk had already started downloading. Returning a TargetState
     * rejects the transition as `redirected`, which short-circuits the remaining BEFORE hooks
     * including lazy-load, so a logged-out visitor never fetches the authenticated bundle.
     */
    const criteriaAuth: HookMatchCriteria = {to: (state) => state.data && state.data.requireAuth};
    router.transitionService.onBefore(criteriaAuth, (transition) => {
        if (storage.accessToken || environment.isMaintenance) {
            return true;
        }

        // The URL the visitor actually asked for. `stateService.href(transition.to())` cannot be
        // used here: for a URL-driven deep link `transition.to()` is the wildcard placeholder, so
        // href() would rebuild '/' rather than the requested path.
        const intendedUrl = router.urlService.url();

        if (intendedUrl && intendedUrl.indexOf('source_id') > 0) {
            // Preserved from the original implementation: these links are bounced through a full
            // page load rather than an in-app transition.
            window.location.href =
                `${window.location.origin}/#/login?redirectUrl=${encodeURIComponent(intendedUrl)}`;
            return false;
        }

        return routingService.target(
            STATE_NAMES.login,
            {redirectUrl: intendedUrl && intendedUrl !== '/' ? intendedUrl : null},
            {reload: true}
        );
    }, {priority: 10});

    //Redirect to dashboard if we are already authenticated
    const criteria = {to: `${STATE_NAMES.login}.*`};
    router.transitionService.onBefore(criteria, (transition) => {
        const value = storage.accessToken;
        return new Promise((resolve) => {
            if (value) {
                resolve(routingService.target(STATE_NAMES.home));
            } else {
                resolve(true);
            }
        });
    });

    // Rewrite retired 2021 registration links to the 2026 plan that replaces them.
    router.transitionService.onBefore({to: STATE_NAMES.signUp}, (transition) => {
        const replacement = resolveLegacyBillingPlan(transition.params().subscription);
        if (!replacement) {
            return true;
        }
        return routingService.target(
            STATE_NAMES.signUp,
            {...transition.params(), subscription: replacement},
            {location: 'replace'}
        );
    });
}
